import { useState, useEffect, useRef } from "react";
import { useServerStore } from "../../stores/serverStore.js";
import { useModalStore } from "../../stores/modalStore.js";
import { useChannelStore } from "../../stores/channelStore.js";

const THEMES = [
  { id: "cyberpunk", label: "AeThex",   color: "#00ffff" },
  { id: "og",        label: "OG",        color: "#7289da" },
  { id: "midnight",  label: "Midnight",  color: "#9933ff" },
  { id: "stealth",   label: "Stealth",   color: "#00ff88" },
  { id: "inferno",   label: "Inferno",   color: "#ff5000" },
];

function ServerIcon({ srv, active, unread, mention, onClick, onDragStart, onDragOver, onDrop, dragging }) {
  const isDiscord = srv.source === "discord";

  let iconClass = "community";
  let officialBadge = null;
  if (srv._demo) {
    if (srv.id === 'demo-foundation') iconClass = "foundation";
    else if (srv.id === 'demo-labs') iconClass = "labs";
    else if (srv.id === 'demo-corp') iconClass = "corporation";
    officialBadge = (
      <span style={{
        position: 'absolute', bottom: -3, right: -3,
        width: 14, height: 14, background: '#00e5ff', borderRadius: '50%',
        fontSize: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#000', border: '2px solid rgb(var(--aethex-bg))', fontWeight: 900,
      }}>✓</span>
    );
  }

  const label = srv._demo
    ? (srv.id === 'demo-foundation' ? 'F' : srv.id === 'demo-labs' ? 'L' : 'C')
    : (srv.name || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div
      style={{ position: "relative", opacity: dragging ? 0.4 : 1, transition: 'opacity 0.15s' }}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div
        onClick={onClick}
        className={`server-icon ${iconClass}${active ? " active" : ""}${isDiscord ? " discord-bridged" : ""}`}
        title={`${srv.name}${isDiscord ? " (Discord)" : ""}${srv._demo ? " · Official" : ""}`}
        style={{ position: "relative" }}
      >
        {srv.icon_url ? (
          <img
            src={srv.icon_url}
            alt={srv.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }}
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          />
        ) : null}
        <span style={{
          display: srv.icon_url ? "none" : "flex",
          alignItems: "center", justifyContent: "center",
          width: "100%", height: "100%",
          fontSize: srv._demo ? "1.1em" : (label.length > 1 ? "0.75em" : "1em"),
          fontWeight: 900,
          color: srv._demo ? '#fff' : undefined,
          textShadow: srv._demo ? '0 1px 4px rgba(0,0,0,0.5)' : undefined,
        }}>
          {label}
        </span>
        {officialBadge}
        {isDiscord && (
          <span style={{ position: "absolute", bottom: -2, right: -2, width: 12, height: 12, background: "#5865f2", borderRadius: "50%", fontSize: 7, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", border: "1px solid rgb(var(--aethex-bg))" }}>
            D
          </span>
        )}
      </div>

      {/* Unread dot — left rail pip */}
      {!active && (unread > 0 || mention > 0) && (
        <span style={{
          position: 'absolute',
          left: -6,
          top: '50%',
          transform: 'translateY(-50%)',
          width: mention > 0 ? 8 : 6,
          height: mention > 0 ? 8 : 6,
          borderRadius: '50%',
          background: mention > 0 ? '#ef4444' : '#fff',
          boxShadow: mention > 0 ? '0 0 6px rgba(239,68,68,0.6)' : 'none',
          pointerEvents: 'none',
        }} />
      )}
    </div>
  );
}

function ThemeSwitcher() {
  const [open, setOpen] = useState(false);
  const [activeTheme, setActiveTheme] = useState(
    () => localStorage.getItem("aethex-theme") || "cyberpunk"
  );
  const ref = useRef(null);

  const applyTheme = (themeId) => {
    document.documentElement.setAttribute("data-theme", themeId);
    localStorage.setItem("aethex-theme", themeId);
    setActiveTheme(themeId);
    setOpen(false);
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="theme-switcher" ref={ref}>
      <button className="theme-switcher-btn" title="Switch theme" onClick={() => setOpen((o) => !o)} aria-label="Theme switcher">
        🎨
      </button>
      {open && (
        <div className="theme-dropdown">
          {THEMES.map((t) => (
            <button key={t.id} className={`theme-option${activeTheme === t.id ? " active" : ""}`} onClick={() => applyTheme(t.id)}>
              <span className="theme-dot" style={{ background: t.color }} />
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ServerList() {
  const { servers, currentServerId, setCurrentServer } = useServerStore();
  const serverUnreads = useChannelStore(s => s.serverUnreads);
  const { onOpen } = useModalStore();

  const [orderedIds, setOrderedIds] = useState([]);
  const dragSrc = useRef(null);

  // Sync orderedIds when servers change (new server added, etc.)
  useEffect(() => {
    const saved = (() => { try { return JSON.parse(localStorage.getItem('server-order') || '[]'); } catch { return []; } })();
    const allIds = servers.map(s => s.id);
    const merged = [...saved.filter(id => allIds.includes(id)), ...allIds.filter(id => !saved.includes(id))];
    setOrderedIds(merged);
  }, [servers]);

  const orderedServers = orderedIds.map(id => servers.find(s => s.id === id)).filter(Boolean);
  const nativeServers  = orderedServers.filter(s => s.source !== "discord");
  const discordServers = orderedServers.filter(s => s.source === "discord");

  const handleDragStart = (id) => { dragSrc.current = id; };
  const handleDrop = (targetId) => {
    if (!dragSrc.current || dragSrc.current === targetId) return;
    setOrderedIds(prev => {
      const next = [...prev];
      const from = next.indexOf(dragSrc.current);
      const to   = next.indexOf(targetId);
      if (from === -1 || to === -1) return prev;
      next.splice(from, 1);
      next.splice(to, 0, dragSrc.current);
      localStorage.setItem('server-order', JSON.stringify(next));
      return next;
    });
    dragSrc.current = null;
  };

  const handleServerClick = (serverId) => {
    if (serverId === "add") { onOpen("createServer"); return; }
    setCurrentServer(serverId);
  };

  const renderIcon = (srv) => (
    <ServerIcon
      key={srv.id}
      srv={srv}
      active={currentServerId === srv.id}
      unread={serverUnreads[srv.id] || 0}
      mention={0}
      onClick={() => handleServerClick(srv.id)}
      onDragStart={() => handleDragStart(srv.id)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => handleDrop(srv.id)}
      dragging={dragSrc.current === srv.id}
    />
  );

  return (
    <div className="server-list">
      {nativeServers.map(renderIcon)}

      {nativeServers.length === 0 && discordServers.length === 0 && (
        <div
          title="Create your first server"
          style={{
            width: 44, height: 44, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px dashed rgb(var(--aethex-primary)/0.5)', color: 'rgb(var(--aethex-primary))',
            cursor: 'pointer', fontSize: 22, animation: 'pulse 2s infinite', transition: 'all 0.2s',
          }}
          onClick={() => onOpen("createServer")}
        >
          +
        </div>
      )}

      {discordServers.length > 0 && (
        <>
          <div className="server-divider" />
          {discordServers.map(renderIcon)}
        </>
      )}

      <div className="server-divider" />

      <div onClick={() => handleServerClick("add")} className="server-icon community" title="Create or join server">+</div>

      <div style={{ flex: 1 }} />
      <ThemeSwitcher />
    </div>
  );
}
